import type { AuthStrategy } from "./types";

/**
 * Canonical, declarative registry for every auth strategy the kit
 * supports. Single source of truth — adding a new strategy means
 * adding ONE entry here and ONE interface in `types.ts`; every
 * downstream surface (server credentials route, web connect dialog,
 * catalog row labels) derives everything else from this table.
 *
 * No other module in the workspace may hand-write the list of auth
 * kinds or their labels.
 */

export type AuthStrategyKind = AuthStrategy["kind"];

/**
 * How a credential of this kind gets created. Drives both the
 * server's `POST /v1/credentials` accept list and the web's
 * connect-dialog grouping.
 *
 *   - `oauth_redirect`     — browser redirect to upstream's authorize URL.
 *   - `client_credentials` — server-to-server token exchange, no user redirect.
 *   - `user_supplied`      — user enters secret(s) in a form (api_key, bearer_token, basic).
 *   - `framework_managed`  — handled out of band (IAM role, browser session, none).
 */
export type ConnectMode =
  | "oauth_redirect"
  | "client_credentials"
  | "user_supplied"
  | "framework_managed";

export interface AuthStrategyInfo {
  kind: AuthStrategyKind;
  /** Human label used everywhere a kind is rendered. */
  label: string;
  connectMode: ConnectMode;
  /**
   * `true` when the runtime strategy carries a `fields` array the
   * user fills in (api_key / bearer_token / basic / oauth2_cc).
   * Drives the field-validation gate on POST /v1/credentials and the
   * form rendering in the connect dialog.
   */
  expectsUserFields: boolean;
}

export const AUTH_STRATEGY_INFO: Record<AuthStrategyKind, AuthStrategyInfo> = {
  oauth2: {
    kind: "oauth2",
    label: "OAuth 2.0",
    connectMode: "oauth_redirect",
    expectsUserFields: false,
  },
  oauth2_built_in: {
    kind: "oauth2_built_in",
    label: "Built-in",
    connectMode: "oauth_redirect",
    expectsUserFields: false,
  },
  oauth2_cc: {
    kind: "oauth2_cc",
    label: "Server-to-Server",
    connectMode: "client_credentials",
    expectsUserFields: true,
  },
  api_key: {
    kind: "api_key",
    label: "API Key",
    connectMode: "user_supplied",
    expectsUserFields: true,
  },
  bearer_token: {
    kind: "bearer_token",
    label: "Bearer Token",
    connectMode: "user_supplied",
    expectsUserFields: true,
  },
  basic: {
    kind: "basic",
    label: "Basic",
    connectMode: "user_supplied",
    expectsUserFields: true,
  },
  session: {
    kind: "session",
    label: "Session",
    connectMode: "framework_managed",
    expectsUserFields: false,
  },
  iam: {
    kind: "iam",
    label: "IAM",
    connectMode: "framework_managed",
    expectsUserFields: false,
  },
  none: {
    kind: "none",
    label: "No auth",
    connectMode: "framework_managed",
    expectsUserFields: false,
  },
};

/** Every kind, in declaration order. */
export const ALL_AUTH_KINDS = Object.keys(
  AUTH_STRATEGY_INFO,
) as AuthStrategyKind[];

/**
 * Kinds the credentials endpoint accepts as `authKind` on POST
 * /v1/credentials — anything where the user (or our server) supplies
 * secrets directly. OAuth redirect flows go through /api/oauth/start
 * instead, so they're excluded here.
 */
export const USER_CREATABLE_AUTH_KINDS = ALL_AUTH_KINDS.filter(
  (k) =>
    AUTH_STRATEGY_INFO[k].connectMode === "user_supplied" ||
    AUTH_STRATEGY_INFO[k].connectMode === "client_credentials",
);

/** Kinds whose strategy carries a `fields` array. */
export const FIELD_BEARING_AUTH_KINDS = ALL_AUTH_KINDS.filter(
  (k) => AUTH_STRATEGY_INFO[k].expectsUserFields,
);

/**
 * Pretty-label any auth kind. Every kind the system knows about lives
 * in `AUTH_STRATEGY_INFO`, so this is a simple lookup. The web
 * console, the API responses, and the SDK type generator all flow
 * through this.
 */
export function authLabel(kind: AuthStrategyKind): string {
  return AUTH_STRATEGY_INFO[kind].label;
}
